import React, { useEffect, useState } from 'react';
import {
    Actionsheet,
    Button,
    FlatList,
    HStack,
    Link,
    VStack,
} from 'native-base';
import { useShopStyles } from '~/shared/styles';
import { Caption, H2, H3, H4 } from '~/shared/Typography';
import { SpIcon } from '~/shared/Icon';
import { faClock } from '@fortawesome/free-solid-svg-icons';
import { SBDropdown } from '~/shared/SBDropdown';
import { FullfillmentOptions } from './FullfillmentOptions';
import { isBefore, isAfter, format, parse } from 'date-fns';
import {
    useDutchieStore,
    Hours,
    HoursDay,
    Maybe,
    OrderType,
    FulfillmentOptionsCardProp,
    useI18n,
} from '@springbig/shared';

type ShopStoreBottomSheetProps = {
    isOpen: boolean;
    onClose: () => void;
};

type HoursCardProp = {
    day: string;
    start?: string;
    end?: string;
};

export const ShopStoreBottomSheet = ({ isOpen, onClose }: ShopStoreBottomSheetProps) => {

    const { button_background, button_text, sub_text } = useShopStyles();
    const { retailer } = useDutchieStore();
    const [hours, setHours] = useState<Array<HoursCardProp>>([]);
    const [isStoreOpen, setIsStoreOpen] = useState<boolean>(false);
    const [fullfillmentOptions, setFullfillmentOptions] = useState<Array<FulfillmentOptionsCardProp>>([]);
    const [fullfillmentOption, setFullfillmentOption] = useState();
    const { t } = useI18n();

    const getHoursCard = (day: string, dayHours: Hours) : HoursCardProp => {

        let hoursCard : HoursCardProp = { day };

        if (dayHours) {
            const dayHour = (dayHours[day as keyof Hours]) as HoursDay;
            if (dayHour?.active && dayHour?.start && dayHour?.end) {
                hoursCard = { day, start: dayHour?.start, end: dayHour?.end };
            }
        }

        return hoursCard;
    };

    useEffect(() => {

        if (fullfillmentOption && fullfillmentOption?.hoursKey) {
            const fullfillmentOptionHours : Maybe<Hours> = retailer?.hours[fullfillmentOption.hoursKey];
            const dayOfTheWeek = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
            if (fullfillmentOptionHours) {
                const hoursCard = dayOfTheWeek.map((e) => getHoursCard(e, fullfillmentOptionHours));
                setHours(hoursCard);
            }
        }

    }, [fullfillmentOption]);

    const storeSchedule = (): boolean => {

        const currentHours = retailer?.hours[fullfillmentOption.hoursKey];
        const currentDayOfTheWeek = format(new Date(), 'EEEE');
        const todayHours = currentHours[currentDayOfTheWeek];
        const startHour = parse(todayHours.start, 'h:mm a', new Date());
        const endHour = parse(todayHours.end, 'h:mm a', new Date());

        return todayHours.active && isAfter(new Date(), startHour) && isBefore(new Date(), endHour);
    };

    useEffect(() => {

        if (retailer && fullfillmentOption) {
            setIsStoreOpen(storeSchedule());
        }
    }, [retailer, fullfillmentOption]);

    const colors = {
        open: '#5eb14b',
        close: '#c42f3b',
    };

    useEffect(() => {

        const availableFulfillmentOptions: Array<FulfillmentOptionsCardProp> = [];
        if (retailer?.fulfillmentOptions.pickup) {
            availableFulfillmentOptions.push({ key: OrderType.Pickup, label: 'pickup', hoursKey: 'pickup' });
        }

        if (retailer?.fulfillmentOptions.curbsidePickup) {
            availableFulfillmentOptions.push({ key: OrderType.CurbsidePickup, label: 'curbside pickup', hoursKey: 'curbsidePickup' });
        }

        if (retailer?.fulfillmentOptions.driveThruPickup) {
            availableFulfillmentOptions.push({ key: OrderType.DriveThruPickup, label: 'drive thru pickup', hoursKey: 'driveThruPickup' });
        }

        if (retailer?.fulfillmentOptions.delivery) {
            availableFulfillmentOptions.push({ key: OrderType.Delivery, label: 'delivery', hoursKey: 'delivery' });
        }

        if (fullfillmentOption) {
            setFullfillmentOption(fullfillmentOption);
        }
        else if (availableFulfillmentOptions && availableFulfillmentOptions[0]) {
            setFullfillmentOption(availableFulfillmentOptions[0]);
        }

        setFullfillmentOptions(availableFulfillmentOptions);
    }, [retailer]);

    return (
        <Actionsheet isOpen={isOpen} onClose={onClose} >
            { retailer &&
                <Actionsheet.Content
                    minH={600}
                    overflow={'scroll'}
                    style={{ width: 'clamp(320px, 100vw, 480px)', margin: 'auto' }}
                >
                    <VStack w={'100%'} alignItems={'center'} space={8} py={8}>
                        <H2 my={0}>
                            { retailer?.name }
                        </H2>
                        <HStack space={2} w={'100%'} alignItems={'center'} justifyContent={'space-evenly'}>
                            { retailer.phone &&
                                <Link
                                    bg={button_background}
                                    rounded={'md'}
                                    p={1}
                                    px={4}
                                    w={'100px'}
                                    justifyContent={'center'}
                                    _hover={{ background: button_background }}
                                    isUnderlined={false}
                                    href={`tel:${retailer.phone}`}
                                >
                                    <Caption color={button_text}>
                                        { t('shop.call') }
                                    </Caption>
                                </Link>
                            }
                            { retailer.address &&
                                <Button
                                    bg={button_background}
                                    rounded={'md'}
                                    p={1}
                                    px={4}
                                    w={'100px'}
                                    justifyContent={'center'}
                                    _hover={{ background: button_background }}
                                    onPress={() => {

                                        window.open(`https://www.google.com/maps?q=${retailer.address}`);
                                    }}>
                                    <Caption color={button_text}>
                                        { t('shop.directions') }
                                    </Caption>
                                </Button>
                            }
                        </HStack>
                        <SBDropdown value={fullfillmentOption?.label}>
                            <FullfillmentOptions
                                setFullfillmentOption={setFullfillmentOption}
                                fullfillmentOptions={fullfillmentOptions}
                            />
                        </SBDropdown>
                        <HStack space={2} justifyContent={'center'}>
                            <SpIcon color={isStoreOpen ? colors.open : colors.close} icon={faClock} />
                            <H4 color={isStoreOpen ? colors.open : colors.close}>
                                { isStoreOpen ? 'Open' : 'Close' }
                            </H4>
                        </HStack>
                        <FlatList
                            width={'100%'}
                            data={hours}
                            renderItem={({ item } : { item: HoursCardProp }) =>

                                <HStack
                                    p={2}
                                    w={'100%'}
                                    alignItems={'center'}
                                    justifyContent={'space-between'}
                                    borderBottomWidth={1}
                                    borderBottomColor={button_background}
                                >
                                    <H3 color={sub_text} fontWeight={300}>
                                        { item.day }
                                    </H3>
                                    { item.start && item.end ?
                                        <H3 color={sub_text} fontWeight={300}>
                                            { item.start } - { item.end }
                                        </H3>
                                        :
                                        <H3 color={sub_text} fontWeight={300}>
                                          Closed
                                        </H3>
                                    }
                                </HStack>
                            }
                        />
                    </VStack>
                </Actionsheet.Content>
            }
        </Actionsheet>
    );
};
